import type { RefOrGetter } from "ls/common/types";
import type { OrganizationJs } from "@/generated/models";
import { useQuery } from "@tanstack/vue-query";
import { computed, toValue } from "vue";
import { queries } from "./queries";

export enum FreemiumUsageStatus {
  free,
  nearLimit,
  atLimit,
  overLimit,
}

export function useOrganizationCloudPlusStatus(organization: RefOrGetter<OrganizationJs>) {
  const options = computed(() => {
    return {
      ...queries.cloudPlusStatus(toValue(organization).Id),
    };
  });
  const query = useQuery(options);
  const cloudPlusStatus = computed(() => query.data.value);

  const isSubscribed = computed(() => cloudPlusStatus.value?.IsSubscribed ?? false);
  const documentsCount = computed(() => cloudPlusStatus.value?.DocumentsCount ?? 0);
  const whiteboardsCount = computed(() => cloudPlusStatus.value?.WhiteboardsCount ?? 0);
  const templatesCount = computed(() => cloudPlusStatus.value?.TemplatesCount ?? 0);
  const librariesCount = computed(() => cloudPlusStatus.value?.LibrariesCount ?? 0);
  const documentsLimit = computed(() => cloudPlusStatus.value?.DocumentsLimit ?? 10);
  const whiteboardsLimit = computed(() => cloudPlusStatus.value?.WhiteboardsLimit ?? 3);
  const templatesLimit = computed(() => cloudPlusStatus.value?.TemplatesLimit ?? 1);
  const librariesLimit = computed(() => cloudPlusStatus.value?.LibrariesLimit ?? 1);
  const isWhitelisted = computed(() => cloudPlusStatus.value?.IsWhitelisted ?? false);
  const whitelistExpiration = computed(() => cloudPlusStatus.value?.WhitelistExpiration ?? null);

  const isPending = computed(() => query.isPending.value);

  const isNearLimit = computed(() => {
    const whiteboardPercentage = whiteboardsCount.value / whiteboardsLimit.value * 100;
    const documentPercentage = documentsCount.value / documentsLimit.value * 100;

    return whiteboardPercentage >= 66 || documentPercentage >= 66;
  });

  const status = computed(() => {
    if (cloudPlusStatus.value?.IsFreemiumExceeded) {
      return FreemiumUsageStatus.overLimit;
    }

    if (whiteboardsCount.value === whiteboardsLimit.value || documentsCount.value === documentsLimit.value) {
      return FreemiumUsageStatus.atLimit;
    }

    if (isNearLimit.value) {
      return FreemiumUsageStatus.nearLimit;
    }

    return FreemiumUsageStatus.free;
  });

  return {
    cloudPlusStatus,
    isPending,
    isSubscribed,
    documentsCount,
    whiteboardsCount,
    templatesCount,
    librariesCount,
    documentsLimit,
    whiteboardsLimit,
    templatesLimit,
    librariesLimit,
    isWhitelisted,
    whitelistExpiration,
    status,
  };
}
